// Text-quote + text-position anchoring, à la the W3C Web Annotation model.
//
// Anchors are stored relative to a node's plain text (a `quote`, ~32 chars of
// `prefix`/`suffix` context, and character `start_offset`/`end_offset`) rather
// than as DOM paths. That makes them survive the DOM restructuring between the
// regular `.trix-content` markup and the reveal.js slideshow, where the same
// text is re-chunked into `<section>` slides.

const CONTEXT_LEN = 32;

// Walk a root element's text nodes and build the concatenated plain text plus a
// segment map (offset range -> text node). The concatenation matches
// `root.textContent`, so it is internally consistent for a given DOM.
export function buildIndex(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      const parent = node.parentNode;
      if (parent) {
        const name = parent.nodeName;
        if (name === "SCRIPT" || name === "STYLE") return NodeFilter.FILTER_REJECT;
        if (parent.closest && parent.closest(".hl-popover")) return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const segments = [];
  let text = "";
  let node;
  while ((node = walker.nextNode())) {
    const start = text.length;
    text += node.nodeValue;
    segments.push({ node, start, end: text.length });
  }
  return { text, segments };
}

// Map a DOM point (from a live Selection range) to a character offset in the index.
export function pointToOffset(index, container, offset) {
  if (container.nodeType === Node.TEXT_NODE) {
    const seg = index.segments.find((s) => s.node === container);
    return seg ? seg.start + offset : null;
  }
  // Element container: the boundary sits before childNodes[offset].
  const ref = container.childNodes[offset];
  if (!ref) {
    for (let i = index.segments.length - 1; i >= 0; i--) {
      if (container.contains(index.segments[i].node)) return index.segments[i].end;
    }
    return null;
  }
  for (const seg of index.segments) {
    if (ref === seg.node || (ref.contains && ref.contains(seg.node))) return seg.start;
  }
  return null;
}

// Describe a live selection range as a storable anchor.
export function describeRange(root, range) {
  const index = buildIndex(root);
  const start = pointToOffset(index, range.startContainer, range.startOffset);
  const end = pointToOffset(index, range.endContainer, range.endOffset);
  if (start == null || end == null || end <= start) return null;

  const quote = index.text.slice(start, end);
  if (!quote.trim()) return null;

  return {
    quote,
    prefix: index.text.slice(Math.max(0, start - CONTEXT_LEN), start),
    suffix: index.text.slice(end, Math.min(index.text.length, end + CONTEXT_LEN)),
    start_offset: start,
    end_offset: end,
  };
}

function commonPrefixLen(a, b) {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
}

function commonSuffixLen(a, b) {
  let i = 0;
  while (i < a.length && i < b.length && a[a.length - 1 - i] === b[b.length - 1 - i]) i++;
  return i;
}

// Resolve a stored anchor to a { start, end } offset range in the current DOM.
// Prefers the exact text-position; falls back to a context-scored quote search
// so anchors still land after the text has been re-chunked (e.g. slideshow).
export function locate(index, hl) {
  const text = index.text;
  const quote = hl.quote;
  if (!quote) return null;

  if (
    Number.isInteger(hl.start_offset) &&
    Number.isInteger(hl.end_offset) &&
    text.slice(hl.start_offset, hl.end_offset) === quote
  ) {
    return { start: hl.start_offset, end: hl.end_offset };
  }

  let best = null;
  let from = 0;
  let idx;
  while ((idx = text.indexOf(quote, from)) !== -1) {
    let score = 0;
    if (hl.prefix) {
      score += commonSuffixLen(text.slice(Math.max(0, idx - hl.prefix.length), idx), hl.prefix);
    }
    if (hl.suffix) {
      const after = text.slice(idx + quote.length, idx + quote.length + hl.suffix.length);
      score += commonPrefixLen(after, hl.suffix);
    }
    const dist = Number.isInteger(hl.start_offset) ? Math.abs(idx - hl.start_offset) : 0;
    if (!best || score > best.score || (score === best.score && dist < best.dist)) {
      best = { start: idx, end: idx + quote.length, score, dist };
    }
    from = idx + 1;
  }
  return best ? { start: best.start, end: best.end } : null;
}

// Wrap the [start, end) offset range in <mark> elements, one per spanned text
// node (so a highlight can cross paragraph/element boundaries). Returns the
// created marks. `decorate(mark)` styles/labels each mark.
export function wrapOffsets(root, start, end, decorate) {
  const index = buildIndex(root);
  const marks = [];
  index.segments.forEach((seg) => {
    const s = Math.max(start, seg.start);
    const e = Math.min(end, seg.end);
    if (s >= e) return;
    try {
      const range = document.createRange();
      range.setStart(seg.node, s - seg.start);
      range.setEnd(seg.node, e - seg.start);
      const mark = document.createElement("mark");
      decorate(mark);
      range.surroundContents(mark);
      marks.push(mark);
    } catch (err) {
      /* skip an un-wrappable slice rather than break the whole page */
    }
  });
  return marks;
}
